import _ from 'lodash';
import ELEMENT from 'element-ui/lib/element-ui.common';

// 全部默认配置参数
import xpcConfig from './config';

// 自定义 plugin
import { defaultPlugins, optionalPlugins } from './plugins';

import { customPlugins } from './interface';

// 引入基础组件集合
import components from './components';

import Loading from './components/loading';
import Message from './components/message';
import MessageBox from './element-ui/packages/message-box';
import Notification from './components/notification/index.js';
import initTooltipGlobal from 'vue-xpc/src/components/tooltip/tooltipGlobal';

/**
 * @private 根据外部的override内容，修改XPC注册的组件代码
 *
 * @param {*} cmp XPC中原有的组件
 * @param {*} overrides 外部传入的覆盖内容
 * @returns
 */
const _overrideComponent = (cmp, overrides) => {
  if (typeof overrides === 'object' && overrides.components) {
    overrides = overrides.components;
  }

  if (!overrides || !Array.isArray(overrides) || overrides.length === 0) {
    return;
  }

  overrides
    .filter(item => item.name === cmp.name)
    .forEach(overrideCmp => {
      // 此处需要直接修改内存中的代码，否则XPC组件中的局部引用会存在override不生效
      if (overrideCmp.replaceMode === 'partial') {
        _.merge(cmp.component, overrideCmp.component);
      } else {
        _.keys(cmp.component).forEach(
          key => (cmp.component[key] = overrideCmp.component[key])
        );
      }
    });
};

/**
 * @private 注册XPC组件，允许修改前缀
 *
 * @param {*} cmp XPC中原有的组件
 * @param {*} prefix 外部传入的组件前缀
 */
const _registerComponents = (cmp, prefix, Vue) => {
  // 组件重命名，防止同名覆盖
  Vue.component(cmp.name.replace('Rt', prefix), cmp.component);

  // 如果全局未注册Rt前缀组件，则默认注册Rt组件
  if (!Vue.options.components[cmp.name]) {
    Vue.component(cmp.name, cmp.component);
  }
};

/**
 * @private 注册即将废弃的组件前缀，向上兼容防止报错
 *
 * @param {*} cmp XPC中原有的组件
 * @param {*} prefix 外部传入的组件前缀
 */
const _registerDeprecatedNames = (cmp, prefix, Vue) => {
  // 注册即将废弃的组件名称
  if (Array.isArray(cmp.deprecatedNames)) {
    cmp.deprecatedNames.forEach(name => {
      Vue.component(name.replace('Rt', prefix), cmp.component);

      // 如果全局未注册Rt前缀组件，则默认注册Rt组件
      if (!Vue.options.components[name]) {
        Vue.component(name, cmp.component);
      }
    });
  }
};

/**
 * @private 注册XPC组件
 *
 * @param {*} Vue
 * @param {*} opts
 */
const installComponents = (Vue, opts) => {
  const prefix = xpcConfig.get('componentPrefix');
  const overrides = xpcConfig.get('overrides.components');

  // xpc 组件全局注册
  components.forEach(cmp => {
    // 根据外部传入的overrides，对组件进行覆写
    _overrideComponent(cmp, overrides);

    // 注册当前组件
    _registerComponents(cmp, prefix, Vue);

    // 补充废弃组件
    _registerDeprecatedNames(cmp, prefix, Vue);
  });
};

/**
 * @private 注册插件
 *
 * @param {*} Vue
 * @param {*} opts
 */
const installPlugins = (Vue, opts) => {
  const plugins = [
    ...defaultPlugins,
    ...optionalPlugins.filter(({ name, autoInstall }) => {
      const pluginConfig = xpcConfig.get('plugins')[name.replace('Plugin', '')];
      if (autoInstall) {
        return pluginConfig !== false;
      } else {
        return pluginConfig === true;
      }
    }),
    ...customPlugins
  ];

  plugins.forEach(plugin => Vue.use(plugin, opts));
};

/**
 * @private 注册ElementUI
 *
 * @param {*} Vue
 * @param {*} opts
 */
const installElementUI = (Vue, opts) => {
  const { i18n } = opts;

  // elementui 多语言设置
  Vue.use(
    ELEMENT,
    i18n ? { i18n: (key, value) => i18n.t(key, value) } : undefined
  );
};

/**
 * @private 注册其他项目，如directives等
 *
 * @param {*} Vue
 * @param {*} opts
 */
const installOthers = (Vue, opts) => {
  Vue.use(Loading.directive);

  // 覆盖ElementUI注册的方法
  Vue.prototype.$loading = Loading.service;
  Vue.prototype.$msgbox = MessageBox;
  Vue.prototype.$alert = MessageBox.alert;
  Vue.prototype.$confirm = MessageBox.confirm;
  Vue.prototype.$prompt = MessageBox.prompt;
  Vue.prototype.$notify = Notification;
  Vue.prototype.$message = Message;

  // 全局tooltip
  initTooltipGlobal();
};

/**
 * @public vue-xpc的注册方法
 *
 * @param {*} Vue
 * @param {*} opts
 */
const install = (Vue, opts = {}) => {
  // 合并全局配置参数
  xpcConfig.assign(opts.config);

  // 注册 ElementUI
  installElementUI(Vue, opts);

  // 注册 plugins
  installPlugins(Vue, opts);

  // 注册 components
  installComponents(Vue, opts);

  // 注册其他项目
  installOthers(Vue, opts);
};

export default install;



// WEBPACK FOOTER //
// ./~/vue-xpc/src/install.js